import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, FlipHorizontal2, Trash2 } from 'lucide-react';
import { QualityDots } from '@/components/editor/ImagePropertiesBar';
import { useDesignStore } from '@/stores/designStore';
import { isSystemLayer } from '@/services/canvas/systemLayerManager';
import { useObjectOperations } from '@/hooks/useObjectOperations';
import { useSizeIndicator } from '@/hooks';
import { cn } from '@/lib/utils';
import type { DirectSaleDesktopPanel } from './DirectSaleDesktopRail';
import {
  TEXT_SIZE_MAX,
  TEXT_SIZE_MIN,
  isTextObject,
  useDirectSaleTextStyle,
} from './useDirectSaleTextStyle';

/** The two drawers the bar can ask for. */
export type DirectSaleObjectBarPanel = Extract<
  DirectSaleDesktopPanel,
  'font' | 'color'
>;

interface DirectSaleObjectBarProps {
  /** 'font' or 'color': which drawer the parent should open. */
  onOpenPanel: (panel: DirectSaleObjectBarPanel) => void;
  /** Which of the two is open, for aria-pressed. */
  activePanel: DirectSaleObjectBarPanel | null;
}

const SEGMENT_BUTTON =
  'flex h-9 items-center gap-2 rounded-full px-3 text-sm text-ink-2 transition-colors hover:bg-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';
const ICON_BUTTON =
  'flex h-9 w-9 items-center justify-center rounded-full text-mute transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * The desktop's one editing surface for the selected object: it replaces
 * the text panel's controls and the image action bar, which on the desktop
 * would otherwise compete with the drawer for the space left of the canvas.
 * The bar only reports WHICH drawer should open (font, colour); the parent
 * owns the drawer state and also places the bar, so it can start right of
 * an open drawer. The text itself is edited on the product (double click),
 * hence no text field here.
 */
export function DirectSaleObjectBar({
  onOpenPanel,
  activePanel,
}: DirectSaleObjectBarProps) {
  const { t } = useTranslation();
  const { duplicateSelected, deleteSelected, flipHorizontal } =
    useObjectOperations();
  const { sizeInfo } = useSizeIndicator();
  const textStyle = useDirectSaleTextStyle();

  // Mockup layers and the print-area frame can end up in the selection;
  // they are never the customer's object.
  const selectedObjects = useDesignStore((s) => s.selectedObjects);
  const userObjects = selectedObjects.filter((o) => !isSystemLayer(o.name));
  if (userObjects.length === 0) return null;

  const hasText = userObjects.some(isTextObject);
  const hasImage = userObjects.some((o) => o.type === 'image');
  const hasOther = userObjects.some((o) => !isTextObject(o));
  // Style controls only make sense while the selection is uniform: a mixed
  // selection keeps just the two operations that apply to every object.
  const textOnly = hasText && !hasOther;
  const imageOnly = hasImage && !hasText;

  const quality = imageOnly ? (sizeInfo?.quality ?? null) : null;

  return (
    <div
      role="toolbar"
      aria-label={t('directSale.desktop.editor')}
      // A pill on one line; still a calm shape when a narrow canvas breaks
      // it into two.
      className="flex min-h-12 flex-wrap items-center justify-center gap-x-1 gap-y-1 rounded-[24px] border border-line bg-paper px-2 py-1"
    >
      {textOnly && (
        <>
          <button
            type="button"
            aria-pressed={activePanel === 'font'}
            onClick={() => onOpenPanel('font')}
            className={cn(
              SEGMENT_BUTTON,
              activePanel === 'font' && 'bg-primary-50 text-primary'
            )}
          >
            {t('directSale.design.font')}
          </button>
          <Divider />
          <div className="flex h-9 items-center gap-2 px-2 text-xs text-mute">
            {/* The words frame the slider where there is room; a narrow
                window keeps the bar on one line without them. */}
            <span aria-hidden="true" className="hidden min-[1200px]:inline">
              {t('directSale.desktop.sizeSmall')}
            </span>
            <input
              type="range"
              min={TEXT_SIZE_MIN}
              max={TEXT_SIZE_MAX}
              value={Math.min(
                Math.max(textStyle.fontSize, TEXT_SIZE_MIN),
                TEXT_SIZE_MAX
              )}
              aria-label={t('directSale.desktop.textSize')}
              onChange={(e) => textStyle.setSizeLive(Number(e.target.value))}
              // Every release is handed on: a touch fires touchend AND an
              // emulated mouseup, and the hook commits only a moved slider.
              onMouseUp={textStyle.commitSize}
              onTouchEnd={textStyle.commitSize}
              onKeyUp={textStyle.commitSize}
              className="w-20 accent-primary-500 min-[1200px]:w-28"
            />
            <span aria-hidden="true" className="hidden min-[1200px]:inline">
              {t('directSale.desktop.sizeLarge')}
            </span>
          </div>
          <Divider />
          <button
            type="button"
            aria-pressed={activePanel === 'color'}
            onClick={() => onOpenPanel('color')}
            className={cn(
              SEGMENT_BUTTON,
              activePanel === 'color' && 'bg-primary-50 text-primary'
            )}
          >
            <span
              aria-hidden="true"
              data-testid="object-bar-color-swatch"
              className="h-4 w-4 flex-none rounded-full border border-line bg-paper-3"
              style={
                textStyle.color
                  ? { backgroundColor: textStyle.color }
                  : undefined
              }
            />
            {t('directSale.design.color')}
          </button>
          <Divider />
        </>
      )}

      {quality && (
        <>
          <div className="flex h-9 items-center gap-2 px-3">
            <span className="sr-only">
              {t('directSale.desktop.printQuality')}
            </span>
            <QualityDots level={quality.level} />
            <span className="text-xs" style={{ color: quality.color }}>
              {sizeInfo?.qualityLabel}
            </span>
          </div>
          <Divider />
        </>
      )}

      <div className="flex items-center gap-1">
        {/* Mirroring is an image affair: a mirrored text reads backwards,
            and a mixed selection keeps only what applies to every object. */}
        {imageOnly && (
          <button
            type="button"
            aria-label={t('directSale.desktop.flip')}
            title={t('directSale.desktop.flip')}
            onClick={() => flipHorizontal()}
            className={cn(ICON_BUTTON, 'hover:bg-paper-2 hover:text-ink-2')}
          >
            <FlipHorizontal2 className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          aria-label={t('directSale.design.duplicate')}
          title={t('directSale.design.duplicate')}
          onClick={() => void duplicateSelected()}
          className={cn(ICON_BUTTON, 'hover:bg-paper-2 hover:text-ink-2')}
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={t('directSale.design.delete')}
          title={t('directSale.design.delete')}
          onClick={() => deleteSelected()}
          className={cn(
            ICON_BUTTON,
            'hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] hover:text-danger'
          )}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Thin vertical line between two segments of the pill. */
function Divider(): ReactNode {
  return <span aria-hidden="true" className="h-6 w-px flex-none bg-line" />;
}
